import { Actions, FSpread } from '@client/new/helpers/types';
import { useState } from 'react';
import { isEmpty } from 'lodash';
import uuid4 from 'uuid4';
import {
  NotificationsVM,
  useNotificationsModel,
} from '@client/new/helpers/useNotifcations';
import { bulkUpload } from './upload.hooks';
import { getFailedFiles, getFileNameWihoutExtension } from '../helpers';
import { useGetCreatorsMediaQuery } from 'generated/graphql';
import { UploadMap, UploadState, minFileSizeWarning } from './library.domain';
import { trackEvent } from '@services/tracking';
import { loadVideo } from '@client/new/helpers/load-video-from-file';
import { durationToLabel } from '../helpers';
import { validateVideoFiles } from './upload.helpers';

type PayloadMap = {
  upload: { folderId: number | null; onSuccess?: () => void };
  deleteFile: string;
  editFile: number;
  saveFileUpdate: { index: number; name?: string; timestamp?: number };
  cancelSingleUpload: string;
  onDrop: File[];
  cancel: never;
  reupload: never;
  cancelFileEdit: never;
};

export type UploadVideoWriteModelAction = Actions<PayloadMap>;
export type UploadVideoWriteModelDispatcher = FSpread<PayloadMap>;

type UploadVideoWriteModelState = {
  files: UploadFile[];
  fileInEdit: number | null;
  isUploading: boolean;
  uploadMap: UploadMap;
  uploadFinished: boolean;
  canUpload: boolean;
};

export type UploadVideoWriteModel = UploadVideoWriteModelState & {
  dispatch: UploadVideoWriteModelDispatcher;
  notifications: NotificationsVM;
};

export type UploadFile = {
  file: File;
  videoSrc: string;
  thumbnailTimestamp?: number;
  edited?: boolean;
  error?: string;
  id: string;
  uploadSizeWarning: boolean;
};

export type UploadVideoWriteModelParams = {
  influencerId: number;
};

export const useUploadVideoWriteModel = ({
  influencerId,
}: UploadVideoWriteModelParams): UploadVideoWriteModel => {
  const [uploadMap, setUploadMap] = useState<UploadMap>({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFinished, setUploadFinished] = useState(false);
  const [files, setFiles] = useState<UploadVideoWriteModelState['files']>([]);
  const [fileInEdit, setFileInEdit] = useState<number | null>(null);
  const { pushNotification, ...notifications } = useNotificationsModel();
  const { refetch: refetchVideos } = useGetCreatorsMediaQuery({
    variables: { creatorId: influencerId },
    skip:
      !window.location.pathname.includes('library') ||
      !window.location.pathname.includes(
        `admin/partner/${influencerId}/workout`,
      ),
  });

  const dispatch: UploadVideoWriteModelDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as UploadVideoWriteModelAction;
    switch (action.type) {
      case 'upload': {
        upload(files, action.payload.folderId, action.payload.onSuccess);
        return null;
      }
      case 'onDrop': {
        onDrop(action.payload);
        return null;
      }
      case 'deleteFile': {
        loadVideo(
          files.find((file) => file.file.name === action.payload)!.file,
        ).then((video) =>
          trackEvent({
            type: 'File Deleted',
            payload: {
              'File Type': 'Video',
              'Deleted During': 'Pre-upload',
              'File Length': durationToLabel(video.duration),
            },
          }),
        );

        setFiles(files.filter((f) => f.file.name !== action.payload));
        return null;
      }
      case 'editFile':
        loadVideo(files[action.payload].file).then((video) =>
          trackEvent({
            type: 'File Edited',
            payload: {
              'File Type': 'Video',
              'Edited From': 'Library',
              'Editing During': 'Pre-upload',
              'File Length': durationToLabel(video.duration),
            },
          }),
        );

        setFileInEdit(action.payload);
        return null;
      case 'cancelFileEdit': {
        if (fileInEdit === null) return null;
        setFileInEdit(null);
        return null;
      }
      case 'saveFileUpdate': {
        const { name, index, timestamp } = action.payload;
        if (!name && !timestamp) return null;
        let update = { ...files[index], edited: true };
        const duration = loadVideo(update.file).then((video) => video.duration);

        if (name) {
          const videoExtension = name.split('.').pop();
          const fileName = getFileNameWihoutExtension(name);
          update = {
            ...update,
            file: new File([update.file], `${fileName}.${videoExtension}`, {
              type: update.file.type,
            }),
          };

          duration.then((duration) =>
            trackEvent({
              type: 'Title Edited',
              payload: {
                'File Type': 'Video',
                'Edited From': 'Library',
                'Editing During': 'Pre-upload',
                'File Length': durationToLabel(duration),
              },
            }),
          );
        }
        if (timestamp) {
          update = {
            ...update,
            thumbnailTimestamp: timestamp,
          };

          duration.then((duration) =>
            trackEvent({
              type: 'Thumbnail Edited',
              payload: {
                'File Type': 'Video',
                'Edited From': 'Library',
                'Editing During': 'Pre-upload',
                'File Length': durationToLabel(duration),
              },
            }),
          );
        }

        const updatedFiles = files.map((f, i) => (i !== index ? f : update));
        setFiles(updatedFiles);
        setFileInEdit(null);
        return null;
      }
      case 'cancelSingleUpload': {
        clearUploadMapByFileId(action.payload);
        const updatedFiles = files.filter((f) => f.id !== action.payload);
        setFiles(updatedFiles);
        if (updatedFiles.length === 0) {
          setIsUploading(false);
        }
        return null;
      }
      case 'cancel':
        clearState();
        return null;
      case 'reupload': {
        const filaedFiles = getFailedFiles(files, uploadMap);
        upload(filaedFiles, null);
        return null;
      }
    }
  };

  const onDrop = async (payload: File[]) => {
    const existingNames = files.map((f) => f.file.name);
    const toUpload: UploadFile[] = payload
      .filter((f) => !existingNames.includes(f.name))
      .map((f) => ({
        id: uuid4(),
        file: f,
        videoSrc: URL.createObjectURL(f),
        uploadSizeWarning: f.size > minFileSizeWarning,
      }));
    const validatedUpload = await validateVideoFiles(toUpload);
    !isEmpty(validatedUpload) && setFiles([...files, ...validatedUpload]);
  };

  const clearUploadMapByFileId = (fileId: UploadState['fileId']) => {
    if (uploadMap[fileId]?.muxInstance) {
      uploadMap[fileId].muxInstance.abort();
    }
    setUploadMap((prev) => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
  };

  const clearState = () => {
    setFiles([]);
    setFileInEdit(null);
    setIsUploading(false);
    setUploadFinished(false);

    for (const entry in uploadMap) {
      if (uploadMap[entry]?.muxInstance) {
        uploadMap[entry].muxInstance.abort();
      }
    }
    setUploadMap({});
  };

  const upload = async (
    videos: UploadFile[],
    folderId: number | null,
    onSuccess?: () => void,
  ) => {
    const validVideos = videos.filter((f) => !f.error);
    setIsUploading(true);
    try {
      trackEvent({
        type: 'Video Upload Started',
        payload: { 'Number of Videos': videos.length },
      });

      await bulkUpload({
        setUploadMap,
        uploadMap,
        influencerId,
        videos: validVideos,
        folderId,
      });
    } catch (e) {
      pushNotification('Error', 'Something went wrong!');
    } finally {
      setIsUploading(false);
      setUploadFinished(true);
      refetchVideos();
      onSuccess && onSuccess();
    }
  };
  const invalidUploadFiles = files.filter((f) => f.error);

  return {
    dispatch,
    isUploading,
    files,
    fileInEdit,
    uploadMap,
    notifications,
    uploadFinished,
    canUpload: !isEmpty(files) && files.length !== invalidUploadFiles.length,
  };
};
